import type { FC } from 'react';

import { Button, IconButton, Paper, Snackbar, Typography } from '@mui/material';
import { CloseIcon } from '@truvhq/ui';

import type { UpgradePromoBannerProps } from './UpgradePromoBanner';

export type UpgradePromoToastProps = UpgradePromoBannerProps & {
  onMaybeLater: () => void;
};

/**
 * Bottom-right A/B variant. Renders a dedicated Snackbar so it doesn't move
 * the global notistack stack (which is anchored bottom-center for normal
 * status toasts).
 */
export const UpgradePromoToast: FC<UpgradePromoToastProps> = ({ pitch, onCta, onMaybeLater, onDismiss }) => {
  return (
    <Snackbar
      open
      anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      ContentProps={{ role: 'status', 'aria-live': 'polite' }}
      sx={{ maxWidth: 360 }}
    >
      <Paper elevation={6} className="relative flex flex-col gap-3 rounded-2xl p-5">
        <IconButton
          aria-label="Dismiss upgrade promo"
          className="absolute right-2 top-2 text-grey60"
          onClick={onDismiss}
          size="small"
        >
          <CloseIcon />
        </IconButton>
        <Typography variant="h4" className="pr-8 font-semibold text-grey100">
          {pitch.headline}
        </Typography>
        <ul className="m-0 flex flex-col gap-1 pl-4">
          {pitch.bullets.slice(0, 2).map((bullet) => (
            <li key={bullet} className="text-sm text-grey80">
              {bullet}
            </li>
          ))}
        </ul>
        <div className="mt-2 flex justify-end gap-2">
          <Button onClick={onMaybeLater} variant="text" size="small">
            Maybe later
          </Button>
          <Button onClick={onCta} variant="contained" color="primary" size="small">
            {pitch.ctaLabel}
          </Button>
        </div>
      </Paper>
    </Snackbar>
  );
};
